/**
 * Compras: el remito del proveedor suma al stock.
 *
 * QUE PREGUNTA CONTESTA ESTA PANTALLA
 * -----------------------------------
 * "Llegó mercadería: ¿cómo la meto al sistema sin cargar producto por
 * producto?" Se sube el PDF, la IA lee el encabezado y las líneas, el sistema
 * las empareja con el catálogo, y el wizard pregunta solo lo que no pudo
 * resolver: "¿es este?", "no lo tenés, ¿lo creo?". Aplicar escribe los
 * ingresos de stock y actualiza el costo de reposición bajo un lote que se
 * deshace en un click.
 *
 * Y si no hay remito (un ticket a mano, una foto que no se lee, nada), el
 * botón "Cargar a mano" del encabezado crea la compra vacía y entra al mismo
 * wizard, con las líneas tipeadas de a una. Es el mismo camino que cuando la
 * IA no pudo leer.
 *
 * "Remito" acá es el del proveedor. El remito de VENTA, el que se manda por
 * WhatsApp, es otra cosa y vive en `venta/AccionesRemito.tsx`.
 *
 * LOS PASOS
 * ---------
 *   subida → leyendo → encabezado ("¿es esto?") → líneas (el wizard) →
 *   revisión → aplicado
 *
 * La compra a mano se saltea "¿es esto?": no hay nada leído que verificar.
 * Entra directo a las líneas, con el encabezado en una franja arriba que se
 * puede corregir.
 *
 * El estado de la compra vive en la base. Lo único local es en qué paso está
 * el operador dentro de una compra COMPLETADA, y el id de la compra en curso
 * (localStorage), para que un F5 no la pierda.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { History, MoreHorizontal, PackagePlus, Pencil, PencilLine, Trash2 } from "lucide-react";

import { PageHeader } from "@/components/layout/PageHeader";
import { Pagina } from "@/components/layout/Pagina";
import { Importe } from "@/components/money";
import { Aviso } from "@/components/ui/aviso";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Codigo } from "@/components/ui/codigo";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { IconButton } from "@/components/ui/icon-button";
import { Pasos } from "@/components/ui/pasos";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { DialogProducto } from "@/components/listas/DialogProducto";
import { ProcesoImport } from "@/components/listas/ProcesoImport";
import { CompraManual } from "@/components/compras/CompraManual";
import {
  CompraEnUnaPantalla,
  type FormaElegida,
  type PropuestaParaGuardar,
} from "@/components/compras/CompraEnUnaPantalla";
import { DialogResumenDeCarga, type ResumenDeCarga } from "@/components/compras/ResumenDeCarga";
import { Encabezado } from "@/components/compras/Encabezado";
import { FlujoLineas } from "@/components/compras/FlujoLineas";
import {
  mediosDe,
  proponerImporte,
  type CuentaFinanciera,
  type PagoCobro,
} from "@/components/cobro/logica";
import { BloqueDeuda } from "@/components/compras/BloqueDeuda";
import { Revision, textoDeLoAplicado } from "@/components/compras/Revision";
import { SubidaRemito } from "@/components/compras/SubidaRemito";
import {
  compraRecordada,
  recordarCompra,
  useActualizarEncabezado,
  useActualizarFilaCompra,
  useAgregarFilaCompra,
  useAplicarCompra,
  useCompra,
  useCompras,
  useCrearCompraManual,
  useCrearProveedor,
  useDescartarCompra,
  useRevertirCompra,
  useRevisionCompra,
  useSubirCompra,
  type CambiosEncabezado,
  type CambiosFila,
  type DatosCompraManual,
  type NuevaFila,
} from "@/components/compras/consultas";
import {
  cuentaParaPagar,
  palabrasDeBusqueda,
  faltaPorCubrir,
  ETIQUETA_ESTADO,
  TONO_ESTADO,
  deudaPropuesta,
  esManual,
  faltaCotizacion,
  importeDelHistorial,
  importeEscrito,
  lineasEnOtraMoneda,
  partesDeLaFranja,
  referenciaDe,
  resumenAplicar,
  rubroSugerido,
  sePuedeAplicar,
  sospechaDeDeuda,
  textoDeudaGuardada,
  deudaConfirmadaGuardada,
  tituloDe,
  unidadesQueNoLlegaron,
  verificarTotal,
  type Compra,
  type FilaCompra,
  type ResultadoAplicarCompra,
} from "@/components/compras/logica";
import { EncargosDeLaCompra } from "@/components/encargos/EncargosDeLaCompra";
import {
  asignaciones,
  propuestaDeEncargos,
  type SeleccionEncargos,
} from "@/components/encargos/logica";
import {
  useCajas,
  useConfig,
  useCuentasFinancieras,
  useEncargos,
  useProveedores,
  useRubros,
} from "@/lib/queries";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { aNumero, type Proveedor } from "@/lib/tipos";
import { formatearFecha } from "@/lib/utils";

export const Route = createFileRoute("/compras")({
  validateSearch: (
    busqueda: Record<string, unknown>,
  ): { nuevo?: boolean; manual?: boolean; id?: string } => {
    const nuevo = busqueda.nuevo === true || busqueda.nuevo === "true";
    const manual = busqueda.manual === true || busqueda.manual === "true";
    const id = typeof busqueda.id === "string" && busqueda.id !== "" ? busqueda.id : undefined;
    return {
      ...(nuevo ? { nuevo: true } : {}),
      ...(manual ? { manual: true } : {}),
      ...(id ? { id } : {}),
    };
  },
  component: Compras,
});

type Paso = "encabezado" | "lineas" | "revision";

/** Que pantalla de alta esta abierta cuando no hay compra en curso. */
type Alta = "remito" | "manual" | null;

function Compras(): JSX.Element {
  const search = Route.useSearch();
  const navigate = useNavigate();

  const [compraId, setCompraId] = useState<string | null>(() => search.id ?? compraRecordada());
  const [archivoNombre, setArchivoNombre] = useState<string | null>(null);
  const [progresoSubida, setProgresoSubida] = useState(0);
  // null = el paso inicial de la compra: "¿es esto?" si se leyo un archivo,
  // las lineas si se cargo a mano.
  const [pasoElegido, setPasoElegido] = useState<Paso | null>(null);
  const [filaBuscando, setFilaBuscando] = useState<FilaCompra | null>(null);
  // El popup de lo que hizo la compra (pedido del 05/11/2026).
  const [resumenCarga, setResumenCarga] = useState<ResumenDeCarga | null>(null);
  const [mostrarResumen, setMostrarResumen] = useState(false);
  // El de la carga en curso: el onSuccess de aplicar se armo antes de que el
  // estado cambie y lo veria viejo.
  const paraElResumen = useRef<ResumenDeCarga | null>(null);
  const [ultimoResultado, setUltimoResultado] = useState<ResultadoAplicarCompra | null>(null);
  const [alta, setAlta] = useState<Alta>(
    search.manual === true ? "manual" : search.nuevo === true ? "remito" : null,
  );

  useEffect(() => {
    recordarCompra(compraId);
  }, [compraId]);

  useEffect(() => {
    if (search.id && search.id !== compraId) setCompraId(search.id);
  }, [search.id, compraId]);

  const { data: proveedores } = useProveedores();
  const { data: rubros } = useRubros();
  const { data: config } = useConfig();
  const cotizacion = aNumero(config?.cotizacion_vigente);
  const { data: cuentasPagina } = useCuentasFinancieras();
  const { data: cajas } = useCajas();

  // El bloque de deuda de Revisar. `totalDeuda` es texto porque es lo que hay en
  // el campo: vacio significa "usar el propuesto", no cero.
  // Va atado a la compra, como `cambioEncargos`: sin eso lo tipeado para una
  // factura aparecia en la siguiente (5000 de DST quedaban como US$ 5.000 de
  // Atlas), trababa Aplicar con la sospecha y el dialogo prometia una deuda
  // que la base no iba a anotar.
  const [totalDeudaDe, setTotalDeudaDe] = useState<{ compra: string | null; texto: string }>({
    compra: null,
    texto: "",
  });
  const setTotalDeuda = (texto: string) => setTotalDeudaDe({ compra: compraId, texto });
  const [yaPaga, setYaPaga] = useState(false);
  // «¿La pagaste?» de la pantalla unica, atado a la compra como el resto: sin
  // nada elegido de entrada (con «cuenta corriente» marcada, el que no miraba
  // dejaba una deuda ya pagada).
  const [formaDe, setFormaDe] = useState<{ compra: string | null; forma: FormaElegida | null }>({
    compra: null,
    forma: null,
  });
  const [editandoEncabezado, setEditandoEncabezado] = useState(false);
  const [pagosDeCompra, setPagosDeCompra] = useState<PagoCobro[]>([]);
  // El tilde de «está bien, es lo que le debo» que se toco en esta visita,
  // atado a la compra y al importe: si el importe cambia, hay que volver a
  // mirarlo. Sin tocarlo, manda lo guardado en la compra (0121_9c).
  const [tildeDeuda, setTildeDeuda] = useState<{ clave: string; valor: boolean } | null>(null);
  const cuentas = useMemo<CuentaFinanciera[]>(
    () => (cuentasPagina?.filas ?? []).filter((c) => c.activo !== false),
    [cuentasPagina],
  );

  const subir = useSubirCompra();
  const crearManual = useCrearCompraManual();
  const { data: datosCompra, isError: compraFallo } = useCompra(compraId);
  const compra = datosCompra?.compra ?? null;
  // Sin nada tipeado para esta compra, el campo arranca en lo que ya estaba
  // guardado. Antes arrancaba vacio: lo guardado volvia como texto gris de
  // ejemplo y con solo entrar y salir del campo se borraba (el flete se perdia
  // y la deuda volvia a ser la suma de las lineas).
  const totalDeuda =
    totalDeudaDe.compra === compraId
      ? totalDeudaDe.texto
      : compra !== null && compra.id === compraId
        ? textoDeudaGuardada(compra)
        : "";

  const conFilas = compra?.estado === "COMPLETADO" || compra?.estado === "APLICADO";
  const manual = compra !== null && esManual(compra);
  const paso: Paso = pasoElegido ?? (manual ? "lineas" : "encabezado");
  const setPaso = setPasoElegido;
  const { data: datosRevision } = useRevisionCompra(compraId, conFilas);
  const filas = useMemo(() => datosRevision?.filas ?? [], [datosRevision]);
  const margenes = useMemo(
    () => new Map((datosRevision?.margenes ?? []).map((m) => [m.rubro_id, m.margen])),
    [datosRevision],
  );
  const forma = formaDe.compra === compraId ? formaDe.forma : null;
  function elegirForma(nueva: FormaElegida): void {
    setFormaDe({ compra: compraId, forma: nueva });
    if (nueva !== "OTRA") {
      setYaPaga(false);
      setPagosDeCompra([]);
    }
  }

  const actualizarEncabezado = useActualizarEncabezado(compraId);
  const actualizarFila = useActualizarFilaCompra(compraId);
  const agregarFila = useAgregarFilaCompra(compraId);
  const aplicar = useAplicarCompra(compraId);
  const revertir = useRevertirCompra(compraId);
  const descartar = useDescartarCompra(compraId);
  const crearProveedor = useCrearProveedor();
  const [confirmarDescartar, setConfirmarDescartar] = useState(false);

  const guardando =
    actualizarEncabezado.isPending ||
    actualizarFila.isPending ||
    agregarFila.isPending ||
    crearProveedor.isPending;

  // Una compra aplicada arranca en la revision: es donde esta Deshacer.
  useEffect(() => {
    if (compra?.estado === "APLICADO") setPaso("revision");
  }, [compra?.estado]);

  const resumen = useMemo(() => resumenAplicar(filas), [filas]);

  /*
   * Los encargos que esta factura puede cerrar (0111). La propuesta se arma
   * de nuevo cuando cambian las líneas o los encargos: los más viejos
   * primero, hasta las unidades que llegan. El que carga la confirma o la
   * cambia en la revisión.
   */
  // Si la lista no se pudo leer, no es «no hay encargos»: la revision lo dice
  // y no deja aplicar (antes se aplicaba y la unidad que un cliente espera
  // entraba como stock libre, con el encargo abierto).
  const { data: encargosAbiertos, isError: encargosFallaron } = useEncargos({
    estado: "POR_RECIBIR",
  });
  const propuesta = useMemo(
    () => propuestaDeEncargos(filas, encargosAbiertos?.filas ?? []),
    [filas, encargosAbiertos],
  );
  // Lo que el operador cambió, atado a la compra: otra compra arranca de la
  // propuesta, sin arrastrar tildes ajenos.
  const [cambioEncargos, setCambioEncargos] = useState<{
    compra: string | null;
    seleccion: SeleccionEncargos;
  } | null>(null);
  const seleccionEncargos =
    cambioEncargos && cambioEncargos.compra === compraId ? cambioEncargos.seleccion : propuesta;
  const setSeleccionEncargos = (seleccion: SeleccionEncargos): void =>
    setCambioEncargos({ compra: compraId, seleccion });
  const verificacion = useMemo(
    () => verificarTotal(filas, compra?.total_declarado ?? null),
    [filas, compra?.total_declarado],
  );
  // Con dolares en juego, el dolar es el de la factura y sin el no se aplica.
  // Proveedor en dolares con factura en pesos: el costo pasa a dolares (0101).
  const proveedor = proveedores?.filas.find((p) => p.id === compra?.proveedor_id) ?? null;
  const monedaProveedor = proveedor?.moneda_lista ?? null;
  const sinCotizacionDe =
    compra !== null && faltaCotizacion(compra, filas, monedaProveedor)
      ? (proveedor?.nombre ?? "el proveedor")
      : null;
  const puede = useMemo(
    () => sePuedeAplicar(compra, resumen, verificacion, sinCotizacionDe, encargosFallaron),
    [compra, resumen, verificacion, sinCotizacionDe, encargosFallaron],
  );
  const encargosTildados = useMemo(
    () => asignaciones(seleccionEncargos).length,
    [seleccionEncargos],
  );
  const rubroSugeridoId = useMemo(() => rubroSugerido(filas), [filas]);

  // La deuda que se anota al aplicar, con lo que hay escrito en «Cuánto le
  // debo» aunque todavia no se haya guardado. Si parece de la otra moneda o se
  // pasa mucho de las lineas, Aplicar espera el tilde del bloque de deuda: un
  // importe en pesos en una compra en dolares se aplicaba sin mas.
  const cotizacionDeuda = aNumero(compra?.cotizacion) || cotizacion;
  const propuestaDeuda = compra !== null ? deudaPropuesta(compra, filas) : null;
  const importeDeuda = importeEscrito(totalDeuda) ?? propuestaDeuda?.importe ?? 0;
  const sospechaDeuda =
    compra !== null && compra.proveedor_id !== null && verificacion.cierra && propuestaDeuda
      ? sospechaDeDeuda(
          importeDeuda,
          propuestaDeuda.suma,
          propuestaDeuda.moneda,
          cotizacionDeuda,
          propuestaDeuda.papel,
        )
      : null;
  const claveDeuda = `${compraId ?? ""}|${importeDeuda}`;
  // Antes el tilde era solo estado de la pantalla: al recargar volvia sin
  // marcar y «Aplicar la compra» quedaba trabado sobre un importe ya mirado.
  const deudaConfirmada =
    tildeDeuda !== null && tildeDeuda.clave === claveDeuda
      ? tildeDeuda.valor
      : compra !== null && compra.id === compraId && deudaConfirmadaGuardada(compra, importeDeuda);
  function confirmarDeuda(valor: boolean): void {
    setTildeDeuda({ clave: claveDeuda, valor });
    corregirEncabezado({ deuda_confirmada: valor ? importeDeuda : null });
  }
  const deudaAlAplicar =
    compra !== null &&
    compra.proveedor_id !== null &&
    compra.genera_deuda &&
    propuestaDeuda?.moneda &&
    importeDeuda > 0
      ? {
          importe: importeDeuda,
          moneda: propuestaDeuda.moneda,
          proveedor: proveedor?.nombre ?? "el proveedor",
          conPago: yaPaga && pagosDeCompra.length > 0,
          // Lo que el pago no cubre, igual que «Falta cubrir» en el bloque: con
          // un pago parcial el dialogo decia «con el pago que cargaste» y
          // callaba la deuda que la base si anota.
          falta: yaPaga
            ? faltaPorCubrir(
                importeDeuda,
                pagosDeCompra,
                cuentas,
                propuestaDeuda.moneda,
                cotizacionDeuda,
              )
            : importeDeuda,
        }
      : null;

  function empezarDeNuevo(): void {
    setCompraId(null);
    setArchivoNombre(null);
    setProgresoSubida(0);
    setUltimoResultado(null);
    setPasoElegido(null);
    setAlta("remito");
    subir.reset();
    void navigate({ to: "/compras", search: { nuevo: true } });
  }

  function empezarAMano(): void {
    setCompraId(null);
    setArchivoNombre(null);
    setProgresoSubida(0);
    setUltimoResultado(null);
    setPasoElegido(null);
    setAlta("manual");
    subir.reset();
    void navigate({ to: "/compras", search: { manual: true } });
  }

  function irAlHistorial(): void {
    setCompraId(null);
    setAlta(null);
    setUltimoResultado(null);
    void navigate({ to: "/compras", search: {} });
  }

  function abrirCompra(id: string): void {
    setCompraId(id);
    setPasoElegido(null);
    setUltimoResultado(null);
    void navigate({ to: "/compras", search: { id } });
  }

  function subirArchivo(archivo: File): void {
    setArchivoNombre(archivo.name);
    setProgresoSubida(0);
    setUltimoResultado(null);
    setPasoElegido(null);
    subir.mutate(
      { archivo, onProgreso: setProgresoSubida },
      {
        onSuccess: (respuesta) => {
          setCompraId(respuesta.compra_id);
          void navigate({ to: "/compras", search: { id: respuesta.compra_id } });
        },
        onError: (error) => {
          setArchivoNombre(null);
          mostrarError(error);
        },
      },
    );
  }

  function crearSinRemito(datos: DatosCompraManual): void {
    setArchivoNombre(null);
    setUltimoResultado(null);
    setPasoElegido(null);
    crearManual.mutate(datos, {
      onSuccess: ({ compra: creada }) => {
        setCompraId(creada.id);
        void navigate({ to: "/compras", search: { id: creada.id } });
      },
      onError: mostrarError,
    });
  }

  function corregirEncabezado(cambios: CambiosEncabezado): void {
    actualizarEncabezado.mutate(cambios, { onError: mostrarError });
  }

  function crearProveedorDesdeDocumento(nombre: string): void {
    crearProveedor.mutate(
      { nombre, moneda_lista: compra?.moneda ?? "USD" },
      {
        onSuccess: (proveedor) => {
          actualizarEncabezado.mutate(
            { proveedor_id: proveedor.id },
            {
              onSuccess: () =>
                mostrarExito(
                  `Proveedor «${proveedor.nombre}» creado`,
                  "Los productos se volvieron a emparejar con su código de proveedor.",
                ),
              onError: mostrarError,
            },
          );
        },
        onError: mostrarError,
      },
    );
  }

  async function decidirFila(filaId: number, cambios: CambiosFila): Promise<void> {
    try {
      await actualizarFila.mutateAsync({ filaId, cambios });
    } catch (error) {
      mostrarError(error);
      throw error;
    }
  }

  async function agregarLinea(fila: NuevaFila): Promise<void> {
    try {
      await agregarFila.mutateAsync(fila);
      mostrarExito("Producto agregado", "Se emparejó solo con el catálogo, si había con qué.");
    } catch (error) {
      mostrarError(error);
      throw error;
    }
  }

  function elegirProducto(productoId: number | null): void {
    const fila = filaBuscando;
    if (fila === null) return;
    actualizarFila.mutate(
      {
        filaId: fila.id,
        cambios:
          productoId === null
            ? { producto_id: null, accion: "IGNORAR" }
            : { producto_id: productoId, accion: "INGRESAR" },
      },
      {
        onSuccess: () => setFilaBuscando(null),
        onError: mostrarError,
      },
    );
  }

  /** El pago armado en el bloque de deuda, o nada si no se marco «ya esta paga». */
  function pagoDelBloque(pagos: PagoCobro[]) {
    return pagos.length > 0
      ? {
          pagos: pagos
            .filter((p): p is PagoCobro & { importe: number } => p.importe !== null)
            .map((p) => {
              const cuenta = cuentas.find((c) => c.id === p.cuenta_id);
              const monedaDeuda = compra?.moneda ?? null;
              return {
                cuenta_id: p.cuenta_id,
                medio: p.medio,
                importe: p.importe,
                ...(cuenta && monedaDeuda && cuenta.moneda !== monedaDeuda
                  ? { moneda_deuda: monedaDeuda }
                  : {}),
                ...(compra?.cotizacion !== null && compra?.cotizacion !== undefined
                  ? { cotizacion: aNumero(compra.cotizacion) }
                  : cuenta && monedaDeuda && cuenta.moneda !== monedaDeuda
                    ? // (0155) Pagar en otra moneda exige el dolar de la
                      // conversion: la base ya no cae sola en el del sistema.
                      // Va el mismo con que la pantalla mostro cuanto cubre.
                      { cotizacion: cotizacionDeuda }
                    : {}),
              };
            }),
        }
      : undefined;
  }

  function confirmarAplicar(): void {
    // Con "ya esta paga" marcado se manda el pago junto con el aplicar, y la
    // base asienta la deuda y su cancelacion en el mismo acto.
    aplicarCon(yaPaga ? pagoDelBloque(pagosDeCompra) : undefined);
  }

  /**
   * «Sí, en efectivo / por banco / con Mercado Pago»: todo lo que se debe,
   * desde la cuenta de ese tipo en la moneda de la deuda (o la de pesos). Es el
   * mismo pago que arma el bloque de deuda, de un toque.
   */
  function pagoDeLaForma(): PagoCobro[] {
    if (forma === null || forma === "DEBO" || forma === "OTRA") return [];
    const moneda = propuestaDeuda?.moneda ?? compra?.moneda ?? "ARS";
    const cuenta = cuentaParaPagar(forma, cuentas, moneda);
    if (!cuenta) return [];
    return [
      {
        id: crypto.randomUUID(),
        cuenta_id: cuenta.id,
        medio: mediosDe(cuenta)[0]?.valor ?? "OTRO",
        importe: proponerImporte(importeDeuda, [], cuentas, moneda, cuenta, cotizacionDeuda),
      },
    ];
  }

  /** La pantalla unica: guarda las altas propuestas y aplica con el pago elegido. */
  async function cargarEnUnaPantalla(
    propuestas: PropuestaParaGuardar[],
    resumen: ResumenDeCarga,
  ): Promise<void> {
    paraElResumen.current = resumen;
    try {
      for (const p of propuestas) {
        await actualizarFila.mutateAsync({ filaId: p.filaId, cambios: p.cambios });
      }
    } catch (error) {
      mostrarError(error);
      return;
    }
    if (forma === "OTRA") {
      aplicarCon(yaPaga ? pagoDelBloque(pagosDeCompra) : undefined);
    } else {
      aplicarCon(pagoDelBloque(pagoDeLaForma()));
    }
  }

  function aplicarCon(pago: ReturnType<typeof pagoDelBloque>): void {
    aplicar.mutate(
      { pago, encargos: asignaciones(seleccionEncargos) },
      {
        onSuccess: (resultado) => {
          setUltimoResultado(resultado);
          setPaso("revision");
          setYaPaga(false);
          setPagosDeCompra([]);
          if (paraElResumen.current !== null) {
            setResumenCarga(paraElResumen.current);
            paraElResumen.current = null;
            setMostrarResumen(true);
          } else {
            // El mismo texto que la tarjeta: las que cerraron un encargo no se
            // cuentan como stock.
            mostrarExito(textoDeLoAplicado(resultado), "Si algo quedó mal, Deshacer está arriba.");
          }
        },
        onError: mostrarError,
      },
    );
  }

  function confirmarDescartarCompra(): void {
    descartar.mutate(undefined, {
      onSuccess: () => {
        setConfirmarDescartar(false);
        mostrarExito("Compra descartada", "No tocó el stock. Ya no aparece en el historial.");
        irAlHistorial();
      },
      onError: mostrarError,
    });
  }

  function confirmarRevertir(): void {
    revertir.mutate(undefined, {
      onSuccess: (resultado) => {
        setMostrarResumen(false);
        setUltimoResultado(null);
        setPaso("lineas");
        mostrarExito(
          `Se deshicieron ${resultado.ingresos_borrados} ingresos`,
          resultado.productos_creados_que_quedan > 0
            ? `Los costos volvieron a lo que valían. ${
                resultado.productos_creados_que_quedan === 1
                  ? "El producto creado queda"
                  : `${resultado.productos_creados_que_quedan} productos creados quedan`
              } en el catálogo con stock cero.`
            : "Los costos volvieron a lo que valían.",
        );
      },
      onError: mostrarError,
    });
  }

  const sinCompra = compraId === null && !subir.isPending;
  const mostrandoSubida = sinCompra && alta === "remito";
  const mostrandoManual = sinCompra && alta === "manual";
  const mostrandoHistorial = sinCompra && alta === null;
  const puedeDescartar =
    compra !== null && compra.estado !== "APLICADO" && compra.estado !== "DESCARTADO";
  const proveedorDe = (c: Compra): string | null =>
    proveedores?.filas.find((p) => p.id === c.proveedor_id)?.nombre ?? null;

  return (
    <Pagina alto={mostrandoSubida ? "fijo" : "natural"}>
      <PageHeader
        titulo="Compras"
        icono={PackagePlus}
        acciones={
          <>
            <Button type="button" variant="outline" onClick={empezarAMano}>
              <PencilLine aria-hidden="true" />
              Cargar a mano
            </Button>
            {/* Lo que no crea ni mueve plata va al menu: volver al historial
                y, si hay una compra en curso, descartarla (al final, en rojo). */}
            {!mostrandoHistorial ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton icono={MoreHorizontal} variant="outline" aria-label="Más acciones" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {/* El menu no dimensiona el icono como lo hace Button. */}
                  <DropdownMenuItem onSelect={irAlHistorial}>
                    <History className="size-4" aria-hidden="true" />
                    Ver el historial
                  </DropdownMenuItem>
                  {puedeDescartar ? (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onSelect={() => setConfirmarDescartar(true)}
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                        Descartar
                      </DropdownMenuItem>
                    </>
                  ) : null}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : null}
            <Button type="button" onClick={empezarDeNuevo}>
              <PackagePlus aria-hidden="true" />
              Cargar remito
            </Button>
          </>
        }
      />

      {mostrandoHistorial ? (
        <Historial proveedores={proveedores?.filas ?? []} onAbrir={abrirCompra} />
      ) : null}

      {mostrandoSubida ? (
        <SubidaRemito subiendo={subir.isPending} progreso={progresoSubida} onSubir={subirArchivo} />
      ) : null}

      {mostrandoManual ? (
        <CompraManual
          proveedores={proveedores?.filas ?? []}
          guardando={crearManual.isPending}
          onCrear={crearSinRemito}
          onCancelar={irAlHistorial}
        />
      ) : null}

      {compraFallo && compraId !== null ? (
        <Aviso
          tono="destructive"
          accion={
            <Button type="button" variant="outline" onClick={irAlHistorial}>
              Ver el historial
            </Button>
          }
        >
          No pudimos traer esa compra. Puede que ya no exista.
        </Aviso>
      ) : null}

      {compra?.estado === "DESCARTADO" ? (
        <Aviso
          tono="neutral"
          accion={
            <Button type="button" variant="outline" onClick={irAlHistorial}>
              Ver el historial
            </Button>
          }
        >
          Esta compra fue descartada. No tocó el stock y ya no aparece en el historial.
        </Aviso>
      ) : null}

      {(compraId !== null || subir.isPending) &&
      !conFilas &&
      !compraFallo &&
      compra?.estado !== "ERROR" &&
      compra?.estado !== "DESCARTADO" ? (
        <ProcesoImport
          imp={
            compra
              ? {
                  ...compra,
                  archivo_nombre: compra.archivo_nombre ?? "",
                  archivo_bytes: compra.archivo_bytes ?? 0,
                }
              : null
          }
          archivoNombre={archivoNombre}
          subiendo={subir.isPending}
          progresoSubida={progresoSubida}
          onEmpezarDeNuevo={empezarDeNuevo}
        />
      ) : null}

      {compra?.estado === "ERROR" ? (
        <LecturaFallida
          compra={compra}
          onCargarAMano={() => setPaso("encabezado")}
          onOtro={empezarDeNuevo}
        />
      ) : null}

      {/* Una compra leida y sin aplicar: todo en una pantalla, de lo que dice el
          remito a «Cargar». Aplicada (con Deshacer) o con la lectura fallida
          sigue el camino de antes. */}
      {compra && compra.estado === "COMPLETADO" ? (
        <CompraEnUnaPantalla
          compra={compra}
          filas={filas}
          proveedor={proveedor}
          rubros={rubros?.filas ?? []}
          rubroSugeridoId={rubroSugeridoId}
          margenes={margenes}
          cotizacion={cotizacion}
          monedaProveedor={monedaProveedor}
          cuentas={cuentas}
          resumen={resumen}
          verificacion={verificacion}
          sinCotizacionDe={sinCotizacionDe}
          encargosSinLeer={encargosFallaron}
          deudaPorConfirmar={sospechaDeuda !== null && !deudaConfirmada}
          guardando={guardando}
          aplicando={aplicar.isPending}
          forma={forma}
          onForma={elegirForma}
          editandoEncabezado={editandoEncabezado || compra.proveedor_id === null}
          onEditarEncabezado={setEditandoEncabezado}
          editorEncabezado={
            <Encabezado
              compra={compra}
              filas={filas}
              proveedores={proveedores?.filas ?? []}
              guardando={guardando}
              onProveedor={(id) => corregirEncabezado({ proveedor_id: id })}
              onCrearProveedor={crearProveedorDesdeDocumento}
              onCorregir={corregirEncabezado}
              onContinuar={() => setEditandoEncabezado(false)}
            />
          }
          bloqueDeuda={
            <BloqueDeuda
              compra={compra}
              filas={filas}
              cuentas={cuentas}
              cotizacion={cotizacionDeuda}
              totalTexto={totalDeuda}
              onTotalTexto={setTotalDeuda}
              onTotalConfirmado={(v) => corregirEncabezado({ total_a_pagar: v })}
              yaPaga={yaPaga}
              onYaPaga={setYaPaga}
              pagos={pagosDeCompra}
              onPagos={setPagosDeCompra}
              cajas={cajas ?? []}
              totalNoCierra={!verificacion.cierra}
              confirmada={deudaConfirmada}
              onConfirmada={confirmarDeuda}
            />
          }
          encargos={
            encargosFallaron ? (
              <Aviso tono="destructive">
                No se pudo leer la lista de encargos. Recargá la página: si algo de esta factura lo
                espera un cliente, hay que tildarlo antes de cargar.
              </Aviso>
            ) : (
              <EncargosDeLaCompra
                filas={filas}
                encargos={encargosAbiertos?.filas ?? []}
                seleccion={seleccionEncargos}
                onSeleccion={setSeleccionEncargos}
              />
            )
          }
          onActualizarFila={decidirFila}
          onAgregarFila={agregarLinea}
          onBuscarProducto={setFilaBuscando}
          onCargar={(propuestas, resumen) => void cargarEnUnaPantalla(propuestas, resumen)}
        />
      ) : null}

      {compra && (conFilas || compra.estado === "ERROR") && compra.estado !== "COMPLETADO" ? (
        <>
          {/* Los pasos solo quedan para la lectura fallida, que se carga a mano
              por el camino de antes. La aplicada es «Lo que entró» y Deshacer. */}
          {compra.estado === "ERROR" ? (
            <PasosDeLaCompra
              paso={paso}
              aplicada={false}
              sinEncabezado={manual && paso !== "encabezado"}
            />
          ) : null}

          {paso === "lineas" && manual ? (
            <FranjaEncabezado
              compra={compra}
              proveedor={proveedores?.filas.find((p) => p.id === compra.proveedor_id) ?? null}
              onCorregir={() => setPaso("encabezado")}
            />
          ) : null}

          {paso === "encabezado" ? (
            <Encabezado
              compra={compra}
              filas={filas}
              proveedores={proveedores?.filas ?? []}
              guardando={guardando}
              onProveedor={(id) => corregirEncabezado({ proveedor_id: id })}
              onCrearProveedor={crearProveedorDesdeDocumento}
              onCorregir={corregirEncabezado}
              onContinuar={() => setPaso("lineas")}
            />
          ) : null}

          {paso === "lineas" ? (
            <FlujoLineas
              compra={compra}
              filas={filas}
              cotizacion={cotizacion}
              monedaProveedor={monedaProveedor}
              rubros={rubros?.filas ?? []}
              rubroSugeridoId={rubroSugeridoId}
              guardando={guardando}
              onActualizarFila={decidirFila}
              onAgregarFila={agregarLinea}
              onBuscarProducto={setFilaBuscando}
              onVolver={() => setPaso("encabezado")}
              onListo={() => setPaso("revision")}
            />
          ) : null}

          {paso === "revision" ? (
            <Revision
              compra={compra}
              resumen={resumen}
              puede={puede}
              aplicando={aplicar.isPending}
              revirtiendo={revertir.isPending}
              resultado={ultimoResultado}
              onVolver={() => setPaso("lineas")}
              onAplicar={confirmarAplicar}
              onRevertir={confirmarRevertir}
              encargosTildados={encargosFallaron ? 0 : encargosTildados}
              lineasEnOtraMoneda={lineasEnOtraMoneda(compra, filas).length}
              unidadesQueNoLlegaron={unidadesQueNoLlegaron(filas)}
              deudaAlAplicar={deudaAlAplicar}
              deudaPorConfirmar={sospechaDeuda !== null && !deudaConfirmada}
              encargos={
                compra.estado === "APLICADO" ? null : encargosFallaron ? (
                  <Aviso tono="destructive">
                    No se pudo leer la lista de encargos. Recargá la página: si algo de esta factura
                    lo espera un cliente, hay que tildarlo antes de aplicar.
                  </Aviso>
                ) : (
                  <EncargosDeLaCompra
                    filas={filas}
                    encargos={encargosAbiertos?.filas ?? []}
                    seleccion={seleccionEncargos}
                    onSeleccion={setSeleccionEncargos}
                  />
                )
              }
              deuda={
                compra.estado === "APLICADO" ? null : (
                  <BloqueDeuda
                    compra={compra}
                    filas={filas}
                    cuentas={cuentas}
                    cotizacion={cotizacionDeuda}
                    totalTexto={totalDeuda}
                    onTotalTexto={setTotalDeuda}
                    onTotalConfirmado={(v) => corregirEncabezado({ total_a_pagar: v })}
                    yaPaga={yaPaga}
                    onYaPaga={setYaPaga}
                    pagos={pagosDeCompra}
                    onPagos={setPagosDeCompra}
                    cajas={cajas ?? []}
                    totalNoCierra={!verificacion.cierra}
                    confirmada={deudaConfirmada}
                    onConfirmada={confirmarDeuda}
                  />
                )
              }
            />
          ) : null}
        </>
      ) : null}

      <Dialog open={confirmarDescartar} onOpenChange={setConfirmarDescartar}>
        <DialogContent tamano="sm">
          <DialogHeader>
            <DialogTitle>Descartar esta compra</DialogTitle>
            <DialogDescription>
              {compra ? `${tituloDe(compra, proveedorDe(compra))} · ${referenciaDe(compra)}. ` : ""}
              No tocó el stock, así que no hay nada que deshacer: solo desaparece del historial.
              {filas.length > 0
                ? ` ${filas.length === 1 ? "Se pierde el producto cargado" : `Se pierden los ${filas.length} productos cargados`}.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmarDescartar(false)}
              disabled={descartar.isPending}
            >
              No, seguir con ella
            </Button>
            <Button
              type="button"
              variant="destructive"
              cargando={descartar.isPending}
              onClick={confirmarDescartarCompra}
            >
              Sí, descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DialogResumenDeCarga
        resumen={resumenCarga}
        abierto={mostrarResumen}
        deshaciendo={revertir.isPending}
        onCerrar={() => setMostrarResumen(false)}
        onDeshacer={confirmarRevertir}
      />

      <DialogProducto
        fila={filaBuscando}
        abierto={filaBuscando !== null}
        onAbiertoChange={(a) => {
          if (!a) setFilaBuscando(null);
        }}
        proveedorId={compra?.proveedor_id ?? null}
        guardando={actualizarFila.isPending}
        onElegir={elegirProducto}
        textoInicial={filaBuscando ? palabrasDeBusqueda(filaBuscando.descripcion) : ""}
        incluirDadosDeBaja
        soloDelProveedorAlAbrir={false}
        descripcion={
          filaBuscando
            ? `El remito dice «${filaBuscando.descripcion}». Elegí cuál de tus productos es: lo que vincules queda aprendido para la próxima compra.`
            : ""
        }
      />
    </Pagina>
  );
}

const PASOS: ReadonlyArray<{ clave: Paso; titulo: string }> = [
  { clave: "encabezado", titulo: "¿Es esto?" },
  { clave: "lineas", titulo: "Los productos" },
  { clave: "revision", titulo: "Revisar y aplicar" },
];

/**
 * Donde esta la compra. Es un indicador, no un filtro: se avanza y se vuelve
 * con los botones de cada paso.
 */
function PasosDeLaCompra({
  paso,
  aplicada,
  sinEncabezado,
}: {
  paso: Paso;
  aplicada: boolean;
  /** Compra a mano: "¿Es esto?" no es un paso, es una franja corregible. */
  sinEncabezado: boolean;
}): JSX.Element {
  const pasos = sinEncabezado ? PASOS.filter((p) => p.clave !== "encabezado") : PASOS;
  const titulos = pasos.map((p) => (aplicada && p.clave === "revision" ? "Aplicada" : p.titulo));
  const actual = Math.max(
    0,
    pasos.findIndex((p) => p.clave === paso),
  );
  return <Pasos pasos={titulos} actual={actual} />;
}

/** El encabezado de una compra a mano, en una linea. Corregir abre el formulario completo. */
function FranjaEncabezado({
  compra,
  proveedor,
  onCorregir,
}: {
  compra: Compra;
  proveedor: Proveedor | null;
  onCorregir: () => void;
}): JSX.Element {
  const partes = partesDeLaFranja(compra, proveedor?.nombre ?? null);
  return (
    <Card padding="compacto" className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm">
        <span className="font-medium">Compra a mano</span>
        <span className="text-muted-foreground"> · {partes.join(" · ")}</span>
      </p>
      <Button type="button" variant="ghost" size="sm" onClick={onCorregir}>
        <Pencil aria-hidden="true" />
        Corregir
      </Button>
    </Card>
  );
}

/** La IA no pudo leer el archivo. Que se caiga la IA no frena la mercadería. */
function LecturaFallida({
  compra,
  onCargarAMano,
  onOtro,
}: {
  compra: Compra;
  onCargarAMano: () => void;
  onOtro: () => void;
}): JSX.Element {
  return (
    <Card tono="destructive" padding="normal" className="space-y-3">
      <TituloSeccion descripcion={compra.error_mensaje ?? "La lectura falló sin dejar detalle."}>
        No pudimos leer «{compra.archivo_nombre ?? "el archivo"}»
      </TituloSeccion>
      {compra.error_codigo ? (
        <p className="text-xs text-muted-foreground">
          Código: <Codigo>{compra.error_codigo}</Codigo>
        </p>
      ) : null}
      <p className="text-sm">
        Podés cargar el encabezado y los productos a mano, con el papel adelante. Es más tipeo, pero
        la mercadería entra igual.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={onCargarAMano}>
          Cargar a mano
        </Button>
        <Button type="button" variant="outline" onClick={onOtro}>
          Probar con otro archivo
        </Button>
      </div>
    </Card>
  );
}

function Historial({
  proveedores,
  onAbrir,
}: {
  proveedores: ReadonlyArray<Proveedor>;
  onAbrir: (id: string) => void;
}): JSX.Element {
  const { data, isFetching } = useCompras();
  const compras = data?.compras ?? [];
  const nombreDe = (c: Compra): string | null =>
    proveedores.find((p) => p.id === c.proveedor_id)?.nombre ?? null;

  if (compras.length === 0) {
    return isFetching ? (
      <Cargando texto="Trayendo las compras…" />
    ) : (
      <EstadoVacio
        icono={PackagePlus}
        titulo="Todavía no cargaste ningún remito"
        detalle="Empezá con «Cargar remito»."
      />
    );
  }

  return (
    <Card padding="none" className="divide-y divide-border overflow-hidden">
      {compras.map((c) => (
        // <button> crudo: fila entera clickeable del historial; el ring va
        // adentro para que el overflow-hidden de la tarjeta no lo recorte.
        <button
          key={c.id}
          type="button"
          onClick={() => onAbrir(c.id)}
          className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">
              {tituloDe(c, nombreDe(c))} · {referenciaDe(c)}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {/* Siempre dd/mm/aaaa: la fecha del papel venia en ISO y la de
                  carga formateada, y la lista mezclaba las dos. */}
              {formatearFecha(c.fecha_comprobante ?? c.created_at)} · {c.filas_total}{" "}
              {c.filas_total === 1 ? "producto" : "productos"}
              {/* Lo que dice la factura, si no lo que se corrigio a mano, si no
                  la suma de las lineas: las compras a mano no tienen total
                  declarado y se listaban sin importe. */}
              {importeDelHistorial(c) !== null && c.moneda ? (
                <>
                  {" "}
                  · <Importe monto={importeDelHistorial(c) ?? 0} moneda={c.moneda} tamano="sm" />
                </>
              ) : null}
            </span>
          </span>
          <Badge variant={TONO_ESTADO[c.estado]} className="shrink-0">
            {ETIQUETA_ESTADO[c.estado]}
          </Badge>
        </button>
      ))}
    </Card>
  );
}
